import { DataTable } from '@customerio/pluma-components/react';
import type { ComponentProps } from 'react';
import type { Delivery } from '@/services/db/delivery/entity';
import { DeliveryTableError } from './delivery-table-error';
import { type DeliveryEmptyStateProps } from './delivery-empty-state';
import { useDeliveryTable } from './use-delivery-table';

const getRowId = (delivery: Delivery) => delivery.id;
const LOADING_STATE = { rowCount: 8 };

export type DeliveryTableProps = DeliveryEmptyStateProps & {
  deliveries: readonly Delivery[];
  isLoading?: boolean;
  isError?: boolean;
  onReload?: () => void;
  /** Continuation load-more (append semantics owned by the query). */
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** The "Load N new" row at the top (see `utils/new-items.ts`). */
  loadNew?: ComponentProps<typeof DataTable>['loadNew'];
  /** Builds the detail/draft href for a row — supplied by the page. */
  getDeliveryHref: (delivery: Delivery) => string;
  /** Available channels (drives the status-legend header). */
  channels?: readonly string[];
  /** Selected channel (`''` = all). */
  actionType?: string;
  /** Recipient column header (the person taxonomy term varies by plan). */
  recipientHeader?: string;
  hasReadOnlyPermission?: boolean;
  onBulkRetry?: (ids: string[]) => void;
  onBulkSend?: (ids: string[]) => void;
  onBulkDelete?: (ids: string[]) => void;
  onBulkRedeliver?: (ids: string[], options?: { tracked?: boolean }) => void;
  /** Per-row retry (expanded row) — fires directly, no confirmation, unlike bulk retry. */
  onRowRetry?: (ids: string[]) => void;
  /** Per-row delete (expanded row) — fires directly, no confirmation, unlike bulk delete. */
  onRowDelete?: (ids: string[]) => void;
};

/**
 * The Outbox deliveries table (FPS-2589). Presentational: rows + callbacks in
 * via props, no fetching. Renders the four columns, bulk selection (only
 * `isDeliverySelectable` rows, surviving load-more appends), the selected-rows
 * bulk-action bar, and continuation load-more. Row expansion + the "all"
 * (filter-wide) bulk variants are a stacked follow-up. The memoized table
 * inputs + prop defaults live in `useDeliveryTable`.
 */
export function DeliveryTable(props: DeliveryTableProps) {
  const {
    data,
    columns,
    rowSelection,
    setRowSelection,
    bulkActions,
    getRowCanSelect,
    ExpandedRowComponent,
    loadMore,
    EmptyStateComponent,
  } = useDeliveryTable(props);

  if (props.isError === true) return <DeliveryTableError onReload={props.onReload} />;

  // A resolved-but-empty result renders the bare empty state, with no table chrome — matching
  // Ember, where an empty record array is falsy in the list's `{{else}}` branch so the `DataTable`
  // (and its column headers) never render. Only short-circuit once loading has settled, so the
  // initial fetch still shows the skeleton rows rather than flashing the empty state. `hasMore`
  // guards the rare empty-page-with-continuation case (server-side filtering can drop every row on
  // a page that still has more): Ember keeps its "Load more" control alive independently of the
  // empty state, so fall through to the table (which owns that control) rather than stranding it.
  // The same holds for new deliveries: an empty list that can load new ones keeps the table, so the
  // "load new" row has somewhere to render.
  if (props.isLoading !== true && data.length === 0 && props.hasMore !== true && props.loadNew?.canLoad !== true) {
    return <EmptyStateComponent />;
  }

  return (
    <DataTable
      caption="Deliveries"
      data={data}
      columns={columns}
      getRowId={getRowId}
      isLoading={props.isLoading === true}
      loadingState={LOADING_STATE}
      // The zero-row case is handled by the short-circuit above; disable Pluma's built-in empty
      // state so the rare empty-page-with-continuation fall-through shows the load-more control
      // rather than a default "no data" panel.
      withEmptyState={false}
      withSorting={false}
      withRowSelection
      getRowCanSelect={getRowCanSelect}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      bulkActions={bulkActions}
      withRowExpanding
      // v9 defaults getCanExpand() to !!row.subRows?.length; these rows are flat.
      getRowCanExpand={() => true}
      expandedRowComponent={ExpandedRowComponent}
      loadNew={props.loadNew}
      loadMore={loadMore}
    />
  );
}
